import { useEffect, useRef } from 'react';
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
/**
 * Lightweight SSE hook with auto-cleanup on unmount or dependency change.
 * Reconnects with exponential backoff on network errors.
 */
export function useEventSource(url, options) {
    const { enabled, onEvent, onError, maxRetries = 5, EventSourceClass } = options;
    const onEventRef = useRef(onEvent);
    const onErrorRef = useRef(onError);
    onEventRef.current = onEvent;
    onErrorRef.current = onError;
    useEffect(() => {
        if (!url || !enabled)
            return;
        let es = null;
        let retryCount = 0;
        let retryTimer = null;
        let closed = false;
        const Ctor = EventSourceClass ?? EventSource;
        function connect() {
            if (closed || !url)
                return;
            es = new Ctor(url);
            for (const eventType of Object.keys(onEventRef.current)) {
                es.addEventListener(eventType, (event) => {
                    let data;
                    try {
                        data = JSON.parse(event.data);
                    }
                    catch {
                        data = event.data;
                    }
                    const result = onEventRef.current[eventType]?.(data);
                    if (result === 'close') {
                        closed = true;
                        es?.close();
                    }
                });
            }
            es.onopen = () => {
                retryCount = 0;
            };
            es.onerror = (event) => {
                onErrorRef.current?.(event);
                es?.close();
                if (closed || retryCount >= maxRetries)
                    return;
                const delay = Math.min(BASE_DELAY_MS * 2 ** retryCount, MAX_DELAY_MS);
                retryCount++;
                retryTimer = setTimeout(connect, delay);
            };
        }
        connect();
        return () => {
            closed = true;
            es?.close();
            if (retryTimer)
                clearTimeout(retryTimer);
        };
    }, [url, enabled, maxRetries, EventSourceClass]);
}
